import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { Box, Typography, Alert, CircularProgress, Tabs, Tab, Paper, Chip, Tooltip, Button, Menu, MenuItem } from '@mui/material';
import { Download } from 'lucide-react';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import type { Fund } from '@diggens/types';
import { getPreviousPeriod, useFundSubmissions, useFundPeriods, useMetricDefinitions } from '@diggens/shared-hooks';
import { formatMetricValue, downloadCSV, downloadExcel } from '@diggens/shared-utils';
import { SubmissionDetailDrawer } from '../../../components/SubmissionDetailDrawer';
import { DocumentViewerModal } from '../../../components/DocumentViewerModal';
import { SubmissionsEmptyState } from '../../../components/SubmissionsEmptyState';

interface SpreadsheetSubmissionsTabProps {
  fund: Fund;
  viewToggle?: ReactNode;
}

export function SpreadsheetSubmissionsTab({ fund, viewToggle }: SpreadsheetSubmissionsTabProps) {
  const currentPeriod = fund.reporting_frequency ? getPreviousPeriod(fund.reporting_frequency, fund.reporting_timezone || 'UTC') : null;

  const { data: periodOptions = [], isLoading: periodsLoading } = useFundPeriods(fund.id);
  const { data: metricDefinitions = [], isLoading: defsLoading } = useMetricDefinitions(fund.id);

  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [emailBody, setEmailBody] = useState<string | undefined>(undefined);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const selectedPeriod = periodOptions[selectedPeriodIndex] || currentPeriod || '';
  const { data: submissions, isLoading: submissionsLoading } = useFundSubmissions(fund.id, selectedPeriod);

  const isLoading = periodsLoading || defsLoading || submissionsLoading;

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedFileId(null);
    setEmailBody(undefined);
  };

  const handleExport = (format: 'csv' | 'excel') => {
    if (!submissions || submissions.length === 0) {
      alert('No submissions available to export');
      setExportAnchor(null);
      return;
    }

    const exportData: Record<string, any>[] = [];
    submissions.forEach((submission: any) => {
      if (submission.metrics && submission.metrics.length > 0) {
        submission.metrics.forEach((metric: any) => {
          exportData.push({
            Company: submission.company_name || 'Unknown',
            Period: selectedPeriod,
            Metric: metric.metric_definitions?.name || 'Unknown',
            Value: formatMetricValue(metric.value),
            Confidence: metric.confidence ? `${(metric.confidence * 100).toFixed(0)}%` : '-',
            Source: metric.source_type || '-',
            Reviewed: metric.reviewed ? 'Yes' : 'No',
            Status: submission.status || '-',
          });
        });
      }
    });

    if (exportData.length === 0) {
      alert('No metrics found to export');
      setExportAnchor(null);
      return;
    }

    const filename = `${fund.name.replace(/\s+/g, '-')}-${selectedPeriod.replace(' ', '-')}`;

    if (format === 'csv') {
      downloadCSV(exportData, filename);
    } else {
      downloadExcel(exportData, filename);
    }

    setExportAnchor(null);
  };

  const sortedDefinitions = useMemo(() => [...metricDefinitions].filter((d) => !d.archived_at).sort((a, b) => a.display_order - b.display_order), [metricDefinitions]);

  const getMetricValue = (companyMetrics: any[] | undefined, definitionId: string) => {
    if (!companyMetrics) return null;
    return companyMetrics.find((m) => m.metric_definitions?.id === definitionId);
  };

  // Build columns for DataGrid
  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: 'company',
        headerName: 'Company',
        width: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: '"Roboto Mono", monospace' }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const status = params.value || 'draft';
          const colorMap = {
            completed: '#10B981',
            processing: '#F59E0B',
            needs_review: '#F43F5E',
            submitted: '#3FAD9F',
            draft: '#6B7280',
          };
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"Roboto Mono", monospace',
                color: colorMap[status as keyof typeof colorMap] || '#6B7280',
              }}
            >
              {status}
            </Typography>
          );
        },
      },
    ];

    // Add metric columns
    sortedDefinitions.forEach((def) => {
      cols.push({
        field: `metric_${def.id}`,
        headerName: def.name,
        width: 130,
        align: 'right',
        headerAlign: 'right',
        description: def.description || def.name,
        sortComparator: (v1: any, v2: any) => {
          // Extract numeric values for sorting
          const getValue = (v: any) => {
            if (!v || !v.value) return -Infinity;
            if (typeof v.value === 'object') {
              if (v.value.amount !== undefined) return v.value.amount;
              if (v.value.percentage !== undefined) return v.value.percentage;
              if (v.value.value !== undefined) return v.value.value;
              if (v.value.months !== undefined) return v.value.months || -Infinity;
            }
            return Number(v.value) || -Infinity;
          };
          return getValue(v1) - getValue(v2);
        },
        renderCell: (params: GridRenderCellParams) => {
          const cellData = params.value as { value: any; confidence: number | null } | null;
          if (!cellData) {
            return null;
          }
          const formattedValue = formatMetricValue(cellData.value);
          if (!formattedValue) {
            return null;
          }
          const confidence = cellData.confidence ? cellData.confidence * 100 : null;
          const color = confidence !== null && confidence < 75 ? 'warning.main' : 'text.primary';
          return (
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2">
                    <strong>Confidence:</strong> {confidence?.toFixed(0) ?? '-'}%
                  </Typography>
                </Box>
              }
              arrow
            >
              <Typography
                variant="body2"
                sx={{
                  color,
                  cursor: 'help',
                  borderBottom: '1px dotted',
                  borderColor: 'divider',
                  fontFamily: '"Roboto Mono", monospace',
                }}
              >
                {formattedValue}
              </Typography>
            </Tooltip>
          );
        },
      });
    });

    return cols;
  }, [sortedDefinitions]);

  // Build rows for DataGrid
  const rows = useMemo(() => {
    if (!submissions) return [];

    return submissions.map((submission) => {
      const row: Record<string, any> = {
        id: submission.company_id,
        company: submission.company_name,
        status: submission.status,
        submissionId: submission.submission_id,
      };

      // Add metric values
      sortedDefinitions.forEach((def) => {
        const metric = getMetricValue(submission.metrics, def.id);
        row[`metric_${def.id}`] = metric ? { value: metric.value, confidence: metric.confidence } : null;
      });

      return row;
    });
  }, [submissions, sortedDefinitions]);

  // Scroll to selected row when selectedSubmissionId changes
  useEffect(() => {
    if (selectedSubmissionId && rows.length > 0) {
      const rowIndex = rows.findIndex((row) => row.submissionId === selectedSubmissionId);
      if (rowIndex !== -1) {
        const targetRow = rows[rowIndex];
        // Use a small timeout to ensure the DataGrid is rendered
        setTimeout(() => {
          if (!targetRow) return;
          const rowElement = document.querySelector(`[data-id="${targetRow.id}"]`) as HTMLElement;
          const virtualScroller = document.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;

          if (rowElement && virtualScroller) {
            const rowTop = rowElement.offsetTop;
            const scrollerHeight = virtualScroller.clientHeight;
            const rowHeight = rowElement.clientHeight;

            // Calculate scroll position to center the row vertically
            const scrollTo = rowTop - scrollerHeight / 2 + rowHeight / 2;

            virtualScroller.scrollTo({
              top: scrollTo,
              behavior: 'smooth',
            });
          }
        }, 100);
      }
    }
  }, [selectedSubmissionId, rows]);

  if (!fund.reporting_frequency || !currentPeriod) {
    return <Alert severity="warning">This fund does not have a reporting frequency configured. Please configure the reporting settings to enable data collection.</Alert>;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (periodOptions.length === 0) {
    return <SubmissionsEmptyState fund={fund} />;
  }

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Period Tabs with View Toggle */}
      <Paper sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        {viewToggle && <Box sx={{ pl: 2, pr: 1 }}>{viewToggle}</Box>}
        <Tabs
          value={selectedPeriodIndex}
          onChange={(_, newValue) => setSelectedPeriodIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flex: 1,
            '& .MuiTab-root': {
              minWidth: 100,
              textTransform: 'none',
            },
          }}
        >
          {periodOptions.map((period, index) => (
            <Tab
              key={period}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {period}
                  {period === currentPeriod && <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                </Box>
              }
              value={index}
            />
          ))}
        </Tabs>
        <Box sx={{ px: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download size={14} strokeWidth={1} />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.5 }}
          >
            Export
          </Button>
          <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
            <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* DataGrid */}
      <Paper sx={{ flex: 1, overflow: 'hidden' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          onRowClick={(params) => {
            const submissionId = params.row.submissionId;
            if (submissionId) {
              setSelectedSubmissionId(submissionId);
            }
          }}
          getRowClassName={(params) => {
            return selectedSubmissionId && params.row.submissionId === selectedSubmissionId ? 'selected-row' : '';
          }}
          initialState={{}}
          sx={{
            border: 'none',
            fontFamily: '"Roboto Mono", monospace',
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:nth-of-type(even)': {
                backgroundColor: 'action.hover',
              },
              '&.selected-row': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiDataGrid-cell': {
                  color: 'primary.contrastText',
                },
              },
            },
            '& .MuiDataGrid-cell': {
              fontFamily: '"Roboto Mono", monospace',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader': {
              fontFamily: '"Roboto Mono", monospace',
            },
          }}
        />
      </Paper>

      {/* Summary row - sticky at bottom */}
      {submissions && submissions.length > 0 && (
        <Paper sx={{ p: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Typography variant="body2">
              <strong>Total Companies:</strong> {submissions.length}
            </Typography>
            <Typography variant="body2">
              <strong>Completed:</strong> {submissions.filter((s) => s.status === 'completed').length}
            </Typography>
            <Typography variant="body2">
              <strong>Processing:</strong> {submissions.filter((s) => s.status === 'processing').length}
            </Typography>
            <Typography variant="body2">
              <strong>Needs Review:</strong> {submissions.filter((s) => s.status === 'needs_review').length}
            </Typography>
            <Typography variant="body2">
              <strong>Not Started:</strong> {submissions.filter((s) => !s.status || s.status === 'draft').length}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Submission Detail Drawer */}
      <SubmissionDetailDrawer
        submissionId={selectedSubmissionId}
        open={!!selectedSubmissionId}
        onClose={() => setSelectedSubmissionId(null)}
        onNavigate={setSelectedSubmissionId}
        submissionIds={submissions?.map((s) => s.submission_id).filter((id): id is string => !!id) || []}
        fundId={fund.id}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal open={viewerOpen} onClose={handleCloseViewer} fileId={selectedFileId} submissionId={selectedSubmissionId} emailBody={emailBody} fundId={fund.id} />
    </Box>
  );
}
